import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Assets.css';
import {
  assetMatchesIntuneComplianceFilter,
  getIntuneComplianceFilterLabel
} from './utils/intuneCompliance';

const ASSET_TYPES = ['Computer', 'Mobile', 'Server', 'Monitor', 'Peripheral', 'Docking Station'];
const ASSET_STATES = ['In Use', 'In Stock', 'In Transit', 'Retired'];
const COLUMN_PREFS_KEY = 'assets-visible-columns';

const Assets = ({ onAssetClick, initialFilters, intuneComplianceFilter: intuneComplianceFilterProp = null }) => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [assets, setAssets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchFilters, setSearchFilters] = useState(() => initialFilters || {});
  const [intuneComplianceFilter, setIntuneComplianceFilter] = useState(intuneComplianceFilterProp);
  const [showDarkDevices, setShowDarkDevices] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ serial: '', name: '', model: '', manufacturer: '', assigned_to: '', asset_state: '', type: '' });
  const [savingAssetStateId, setSavingAssetStateId] = useState(null);
  const [addError, setAddError] = useState(null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addMode, setAddMode] = useState('single');
  const [jsonInput, setJsonInput] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [editingAssignedToId, setEditingAssignedToId] = useState(null);
  const [editAssignedToValue, setEditAssignedToValue] = useState('');
  const [editUserSuggestions, setEditUserSuggestions] = useState([]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const stored = localStorage.getItem(COLUMN_PREFS_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  });

  const fetchAssets = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const includeDark = showDarkDevices ? '&includeDark=1' : '';
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/assets?tenant=${selectedTenant.name}${includeDark}`);

      const data = await response.json();

      if (response.ok) {
        setAssets(data.assets);
        setColumns(data.columns);
      } else {
        setError(data.error || 'Failed to fetch assets');
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch, showDarkDevices]);

  useEffect(() => {
    if (selectedTenant) {
      fetchAssets();
    }
  }, [selectedTenant, fetchAssets]);

  useEffect(() => {
    if (initialFilters && typeof initialFilters === 'object') {
      setSearchFilters(initialFilters);
    }
  }, [initialFilters]);

  useEffect(() => {
    setIntuneComplianceFilter(intuneComplianceFilterProp || null);
  }, [intuneComplianceFilterProp]);

  // Fetch user suggestions for typeahead (add modal)
  useEffect(() => {
    if (!selectedTenant || !userSearchQuery.trim()) {
      setUserSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/tenant-users?tenant=${encodeURIComponent(selectedTenant.name)}&q=${encodeURIComponent(userSearchQuery.trim())}`
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.users)) {
          setUserSuggestions(data.users);
        } else {
          setUserSuggestions([]);
        }
      } catch {
        setUserSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedTenant, userSearchQuery, authenticatedFetch]);

  // Fetch user suggestions for inline edit
  useEffect(() => {
    if (!selectedTenant || !editingAssignedToId || editAssignedToValue.length < 1) {
      setEditUserSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/tenant-users?tenant=${encodeURIComponent(selectedTenant.name)}&q=${encodeURIComponent(editAssignedToValue.trim())}`
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.users)) {
          setEditUserSuggestions(data.users);
        } else {
          setEditUserSuggestions([]);
        }
      } catch {
        setEditUserSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedTenant, editingAssignedToId, editAssignedToValue, authenticatedFetch]);

  // Close column picker when clicking outside
  useEffect(() => {
    if (!showColumnPicker) return;
    const handleClick = (e) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColumnPicker]);

  const formatValue = (value, column) => {
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }
    
    // Format based on column type
    if (column.type && column.type.includes('int')) {
      return value.toString();
    }
    
    if (column.type && column.type.includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type && column.type.includes('datetime')) {
      return new Date(value).toLocaleString();
    }
    
    return value.toString();
  };

  const getColumnDisplayName = (fieldName) => {
    // Convert field names to display names
    const displayNames = {
      'id': 'ID',
      'name': 'Name',
      'serial': 'Serial Number',
      'os-name': 'Operating System',
      'model': 'Model',
      'manufacturer': 'Manufacturer',
      'status': 'Status',
      'location': 'Location',
      'assigned_to': 'Assigned To',
      'asset_state': 'Asset State',
      'purchase_date': 'Purchase Date',
      'warranty_expiry': 'Warranty Expiry',
      'warranty-end': 'Warranty End',
      'ChassisType': 'Chassis Type'
    };
    
    return displayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter out ID column, type column, and unwanted columns, then sort columns
  const filteredColumns = columns.filter(column => {
    const fieldName = column.field.toLowerCase();
    return fieldName !== 'id' && 
           fieldName !== 'type' &&
           fieldName !== 'intune-raw' && 
           fieldName !== 'datto-raw' && 
           fieldName !== 'datto-uid' &&
           fieldName !== 'isactive' &&
           fieldName !== 'lastupdated' &&
           (fieldName === 'warranty-end' || !fieldName.includes('warranty'));
  }).sort((a, b) => {
    const order = ['serial', 'name', 'chassistype', 'model', 'manufacturer', 'assigned_to', 'asset_state'];
    const aField = a.field.toLowerCase();
    const bField = b.field.toLowerCase();
    const aIdx = order.indexOf(aField);
    const bIdx = order.indexOf(bField);
    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
    if (aIdx >= 0) return -1;
    if (bIdx >= 0) return 1;
    return 0;
  });

  // Columns to display (filtered by user visibility preference)
  const visibleColumnsList = filteredColumns.filter(col => {
    const key = col.field.toLowerCase();
    return visibleColumns[key] !== false; // default true when not set
  });
  const displayColumns = visibleColumnsList.length > 0 ? visibleColumnsList : filteredColumns;

  const toggleColumnVisibility = (fieldName) => {
    const key = fieldName.toLowerCase();
    const next = { ...visibleColumns, [key]: visibleColumns[key] === false };
    setVisibleColumns(next);
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next));
    } catch {}
  };

  const selectAllColumns = () => {
    const next = {};
    filteredColumns.forEach(col => { next[col.field.toLowerCase()] = true; });
    setVisibleColumns(next);
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next));
    } catch {}
  };

  const deselectAllColumns = () => {
    const next = {};
    filteredColumns.forEach(col => { next[col.field.toLowerCase()] = false; });
    setVisibleColumns(next);
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next));
    } catch {}
  };

  // Handle search filter changes
  const handleSearchChange = (columnField, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [columnField]: value.toLowerCase()
    }));
  };

  // Clear all search filters
  const clearAllFilters = () => {
    setSearchFilters({});
    setIntuneComplianceFilter(null);
  };

  const hasActiveFilters = Object.keys(searchFilters).length > 0 || Boolean(intuneComplianceFilter);

  // Filter and sort assets based on search filters and sort configuration
  const filteredAndSortedAssets = [...assets]
    .filter(item => {
      if (intuneComplianceFilter) {
        if (!assetMatchesIntuneComplianceFilter(item, intuneComplianceFilter, 'nonCompliant')) {
          return false;
        }
      }

      // Apply search filters
      return Object.entries(searchFilters).every(([columnField, searchValue]) => {
        if (!searchValue) return true; // No filter applied for this column
        
        const itemValue = columnField.toLowerCase() === 'chassistype'
          ? (item.ChassisType || item.type)
          : item[columnField];
        if (itemValue === null || itemValue === undefined) return false;
        
        return String(itemValue).toLowerCase().includes(searchValue);
      });
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const getVal = (x) => sortConfig.key.toLowerCase() === 'chassistype'
        ? (x.ChassisType || x.type)
        : x[sortConfig.key];
      const aValue = getVal(a);
      const bValue = getVal(b);
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Convert to string for comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortConfig.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const openAddModal = () => {
    setShowAddModal(true);
    setAddForm({ serial: '', name: '', model: '', manufacturer: '', assigned_to: '', asset_state: '', type: '' });
    setAddError(null);
    setAddMode('single');
    setJsonInput('');
    setImportResults(null);
    setUserSearchQuery('');
    setUserSuggestions([]);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddError(null);
    setAddSubmitting(false);
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    setAddError(null);
    const { serial, name, model, manufacturer, assigned_to, asset_state, type } = addForm;
    if (!type) {
      setAddError('Please select a chassis type');
      return;
    }
    if (type !== 'Peripheral' && !serial.trim()) {
      setAddError('Serial number is required');
      return;
    }
    setAddSubmitting(true);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serial: serial.trim() || undefined,
            name: name.trim() || undefined,
            model: model.trim() || undefined,
            manufacturer: manufacturer.trim() || undefined,
            assigned_to: assigned_to.trim() || undefined,
            asset_state: asset_state || undefined,
            chassisType: type || undefined
          })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setAddError(data.error || 'Failed to add asset');
        return;
      }
      closeAddModal();
      fetchAssets();
    } catch (err) {
      setAddError('Network error. Please try again.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const parseAndMapJsonDevice = (item) => {
    const manufacturer = (item.SystemManufacturer || '').trim() || null;
    const chassisName = (item.ChassisName || '').trim();
    const type = ASSET_TYPES.includes(chassisName) ? chassisName : 'Peripheral';
    const model = (item.MachineType || '').trim() || null;
    const serial = (item.SerialNumber || item.SerialNumberLong || '').trim() || null;
    const lastConnected = (item.Discovered || '').trim() || null;
    return { manufacturer, type, model, serial, lastConnected };
  };

  const handleImportFromJson = async (e) => {
    e.preventDefault();
    setAddError(null);
    setImportResults(null);

    let parsed;
    try {
      parsed = typeof jsonInput === 'string' ? JSON.parse(jsonInput) : jsonInput;
    } catch {
      setAddError('Invalid JSON. Please check the format.');
      return;
    }

    const peripherals = Array.isArray(parsed?.Peripheral) ? parsed.Peripheral : [];
    if (peripherals.length === 0) {
      setAddError('No "Peripheral" array found in JSON, or it is empty.');
      return;
    }

    const upn = (parsed.UPN || '').trim() || null;

    setAddSubmitting(true);
    const results = { success: [], failed: [] };

    for (const item of peripherals) {
      const { manufacturer, type, model, serial, lastConnected } = parseAndMapJsonDevice(item);
      if (!serial && type !== 'Peripheral') {
        results.failed.push({ item, error: 'Serial number is required' });
        continue;
      }

      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/assets?tenant=${encodeURIComponent(selectedTenant.name)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              serial: serial || undefined,
              model: model || undefined,
              manufacturer: manufacturer || undefined,
              chassisType: type || undefined,
              upn: upn || undefined,
              LastConnected: lastConnected || undefined
            })
          }
        );
        const data = await res.json();
        if (res.ok) {
          results.success.push({ serial: serial || data.serial, model });
        } else {
          results.failed.push({ item, error: data.error || 'Failed to add' });
        }
      } catch (err) {
        results.failed.push({ item, error: 'Network error' });
      }
    }

    setImportResults(results);
    setAddSubmitting(false);
    if (results.success.length > 0) {
      fetchAssets();
    }
  };

  const handleJsonFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result;
        if (typeof text === 'string') setJsonInput(text);
      } catch {}
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const saveAssignedTo = async (assetId, valueOverride) => {
    if (!assetId || !selectedTenant) return;
    const value = (valueOverride !== undefined ? valueOverride : editAssignedToValue || '').trim();
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/assigned-to?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assigned_to: value || null })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update assigned to:', data.error);
        return;
      }
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, assigned_to: value || null } : a));
    } catch (err) {
      console.error('Failed to update assigned to:', err);
    } finally {
      setEditingAssignedToId(null);
      setEditAssignedToValue('');
      setEditUserSuggestions([]);
    }
  };

  const startEditAssignedTo = (asset) => {
    setEditingAssignedToId(asset.id);
    setEditAssignedToValue(asset.assigned_to || '');
    setEditUserSuggestions([]);
  };

  const saveAssetState = async (assetId, value) => {
    if (!assetId || !selectedTenant) return;
    const normalized = value || null;
    setSavingAssetStateId(assetId);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/asset-state?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ asset_state: normalized })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update asset state:', data.error);
        return;
      }
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, asset_state: data.asset_state ?? normalized } : a));
    } catch (err) {
      console.error('Failed to update asset state:', err);
    } finally {
      setSavingAssetStateId(null);
    }
  };

  if (loading) {
    return (
      <div className="assets-container">
        <div className="assets-header">
          <h2>Assets overview</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assets-container">
        <div className="assets-header">
          <h2>Assets overview</h2>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Assets</h3>
          <p>{error}</p>
          <button onClick={fetchAssets} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!selectedTenant) {
    return (
      <div className="assets-container">
        <div className="assets-header">
          <h2>Assets</h2>
        </div>
        <div className="no-tenant-container">
          <div className="no-tenant-icon">🏢</div>
          <h3>No Tenant Selected</h3>
          <p>Please select a tenant from the dropdown to view assets.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assets-container">
      <div className="assets-header">
        <div className="assets-header-left">
          <h2>Assets overview</h2>
          {intuneComplianceFilter && (
            <div className="assets-intune-filter-banner">
              <span>{getIntuneComplianceFilterLabel(intuneComplianceFilter)}</span>
              <button type="button" onClick={() => setIntuneComplianceFilter(null)}>
                Remove filter
              </button>
            </div>
          )}
        </div>
        <div className="assets-actions">
          <button onClick={openAddModal} className="add-asset-button">
            <Icon name="add" size="small" />
            Add Asset
          </button>
          <button onClick={fetchAssets} className="refresh-button">
            <Icon name="refresh" size="small" />
            Refresh
          </button>
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="clear-filters-button">
              Clear Filters
            </button>
          )}
          <label className="dark-devices-toggle">
            <input
              type="checkbox"
              checked={showDarkDevices}
              onChange={(e) => setShowDarkDevices(e.target.checked)}
            />
            <span>Show Dark Devices</span>
          </label>
          <span className="assets-count">
            {filteredAndSortedAssets.length} of {assets.length} asset{assets.length !== 1 ? 's' : ''}
          </span>
          <div className="column-picker-wrapper" ref={columnPickerRef}>
            <button
              type="button"
              className="column-picker-button"
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              title="Select columns"
            >
              <Icon name="settings" size="small" />
            </button>
            {showColumnPicker && (
              <div className="column-picker-dropdown">
                <div className="column-picker-header">Columns</div>
                <div className="column-picker-actions">
                  <button type="button" onClick={selectAllColumns}>Show all</button>
                  <button type="button" onClick={deselectAllColumns}>Hide all</button>
                </div>
                <div className="column-picker-list">
                  {filteredColumns.map((column) => {
                    const key = column.field.toLowerCase();
                    const isVisible = visibleColumns[key] !== false;
                    return (
                      <label key={column.field} className="column-picker-item">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumnVisibility(column.field)}
                        />
                        <span>{getColumnDisplayName(column.field)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {assets.length === 0 ? (
        <div className="no-assets-container">
          <Icon name="assets" size="large" className="no-assets-icon" />
          <h3>No Assets Found</h3>
          <p>No assets found.</p>
        </div>
      ) : (
        <div className="assets-grid-container">
          <div className="assets-grid">
            <table className="assets-table">
              <thead>
                <tr>
                  {displayColumns.map((column) => (
                    <th 
                      key={column.field} 
                      className="table-header sortable"
                      onClick={() => handleSort(column.field)}
                    >
                      <div className="header-content">
                        <span>{getColumnDisplayName(column.field)}</span>
                        {sortConfig.key === column.field && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr className="search-row">
                  {visibleColumnsList.map((column) => (
                    <th key={`search-${column.field}`} className="search-header">
                      <input
                        type="text"
                        placeholder={`Search ${getColumnDisplayName(column.field)}...`}
                        value={searchFilters[column.field] || ''}
                        onChange={(e) => handleSearchChange(column.field, e.target.value)}
                        className="column-search-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedAssets.map((asset, index) => {
                  const isDarkDevice = asset.isActive === 0 || asset.isActive === false || asset.isactive === 0 || asset.isactive === false;
                  return (
                  <tr 
                    key={asset.id || index} 
                    className={`table-row ${isDarkDevice ? 'dark-device-row' : ''}`}
                  >
                    {displayColumns.map((column) => {
                      const isSerialColumn = column.field.toLowerCase() === 'serial';
                      const isAssignedToColumn = column.field.toLowerCase() === 'assigned_to';
                      const isAssetStateColumn = column.field.toLowerCase() === 'asset_state';
                      const cellValue = asset[column.field];
                      const hasValue = cellValue && cellValue !== '';
                      const isEditingThis = isAssignedToColumn && editingAssignedToId === asset.id;

                      const handleCellClick = () => {
                        if (isEditingThis || isAssetStateColumn) return;
                        if (isAssignedToColumn) {
                          startEditAssignedTo(asset);
                          return;
                        }
                        if (isSerialColumn && asset.serial) {
                          onAssetClick && onAssetClick(asset.serial);
                        } else if (!isSerialColumn && hasValue) {
                          handleSearchChange(column.field, cellValue);
                        }
                      };

                      if (isAssetStateColumn) {
                        const currentState = asset.asset_state || '';
                        return (
                          <td key={column.field} className="table-cell asset-state-cell" onClick={(e) => e.stopPropagation()}>
                            <select
                              className="asset-state-select"
                              value={currentState}
                              disabled={savingAssetStateId === asset.id}
                              onChange={(e) => saveAssetState(asset.id, e.target.value || null)}
                            >
                              <option value="">—</option>
                              {ASSET_STATES.map((state) => (
                                <option key={state} value={state}>{state}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }

                      if (isAssignedToColumn && isEditingThis) {
                        return (
                          <td key={column.field} className="table-cell assigned-to-edit-cell" onClick={(e) => e.stopPropagation()}>
                            <div className="assigned-to-typeahead">
                              <input
                                type="text"
                                className="assigned-to-input"
                                value={editAssignedToValue}
                                onChange={(e) => setEditAssignedToValue(e.target.value)}
                                onBlur={() => setTimeout(() => saveAssignedTo(asset.id), 150)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveAssignedTo(asset.id);
                                  if (e.key === 'Escape') {
                                    setEditingAssignedToId(null);
                                    setEditAssignedToValue('');
                                  }
                                }}
                                autoFocus
                                placeholder="Search user..."
                              />
                              {editUserSuggestions.length > 0 && (
                                <ul className="typeahead-dropdown">
                                  {editUserSuggestions.map((u, i) => {
                                    const displayName = u.displayName || u.displayname || u.userPrincipalName || u.userprincipalname || '';
                                    return (
                                    <li
                                      key={i}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setEditAssignedToValue(displayName);
                                        setEditUserSuggestions([]);
                                        saveAssignedTo(asset.id, displayName);
                                      }}
                                    >
                                      {displayName}
                                    </li>
                                  );
                                  })}
                                </ul>
                              )}
                            </div>
                          </td>
                        );
                      }

                      return (
                        <td 
                          key={column.field} 
                          className={`table-cell ${isAssignedToColumn ? 'assigned-to-cell' : ''} ${(isSerialColumn && asset.serial) || (!isSerialColumn && hasValue) ? 'clickable-cell' : ''}`}
                          onClick={handleCellClick}
                          style={{ 
                            cursor: (isSerialColumn && asset.serial) || !isSerialColumn ? 'pointer' : 'default' 
                          }}
                        >
                          {isAssignedToColumn && (
                            <span className="edit-assigned-icon" title="Edit assigned to">
                              <Icon name="edit" size="small" />
                            </span>
                          )}
                          {formatValue(column.field.toLowerCase() === 'chassistype' ? (asset.ChassisType || asset.type) : asset[column.field], column)}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="asset-modal-overlay" onClick={closeAddModal}>
          <div className="asset-modal asset-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Add Asset</h3>
            <div className="asset-modal-tabs">
              <button
                type="button"
                className={`asset-modal-tab ${addMode === 'single' ? 'active' : ''}`}
                onClick={() => { setAddMode('single'); setAddError(null); setImportResults(null); }}
              >
                Add single
              </button>
              <button
                type="button"
                className={`asset-modal-tab ${addMode === 'import' ? 'active' : ''}`}
                onClick={() => { setAddMode('import'); setAddError(null); setImportResults(null); }}
              >
                Import from JSON
              </button>
            </div>

            {addMode === 'single' ? (
            <form onSubmit={handleAddAsset}>
              {addError && <div className="asset-modal-error">{addError}</div>}
              <div className="asset-modal-row">
                <label>Chassis Type *</label>
                <select
                  value={addForm.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setAddForm(f => ({ ...f, type: t, serial: t === 'Peripheral' ? '' : f.serial }));
                  }}
                  required
                >
                  <option value="">-- Select chassis type --</option>
                  {ASSET_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="asset-modal-row">
                <label>Serial Number {addForm.type === 'Peripheral' ? '(auto-generated)' : '*'}</label>
                <input
                  type="text"
                  value={addForm.serial}
                  onChange={(e) => setAddForm(f => ({ ...f, serial: e.target.value }))}
                  disabled={addForm.type === 'Peripheral'}
                  placeholder={addForm.type === 'Peripheral' ? 'Auto-generated' : 'Required'}
                />
              </div>
              <div className="asset-modal-row">
                <label>Device Name</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Device name"
                />
              </div>
              <div className="asset-modal-row">
                <label>Model</label>
                <input
                  type="text"
                  value={addForm.model}
                  onChange={(e) => setAddForm(f => ({ ...f, model: e.target.value }))}
                  placeholder="Model"
                />
              </div>
              <div className="asset-modal-row">
                <label>Manufacturer</label>
                <input
                  type="text"
                  value={addForm.manufacturer}
                  onChange={(e) => setAddForm(f => ({ ...f, manufacturer: e.target.value }))}
                  placeholder="Manufacturer"
                />
              </div>
              <div className="asset-modal-row">
                <label>Asset State</label>
                <select
                  value={addForm.asset_state}
                  onChange={(e) => setAddForm(f => ({ ...f, asset_state: e.target.value }))}
                >
                  <option value="">—</option>
                  {ASSET_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="asset-modal-row asset-modal-typeahead">
                <label>Assigned To</label>
                <div className="typeahead-wrapper">
                  <input
                    type="text"
                    value={addForm.assigned_to}
                    onChange={(e) => {
                      setAddForm(f => ({ ...f, assigned_to: e.target.value }));
                      setUserSearchQuery(e.target.value);
                    }}
                    onFocus={() => userSearchQuery && setUserSuggestions(userSuggestions)}
                    placeholder="Search by display name..."
                  />
                  {userSuggestions.length > 0 && (
                    <ul className="typeahead-dropdown">
                      {userSuggestions.map((u, i) => {
                        const displayName = u.displayName || u.displayname || u.userPrincipalName || u.userprincipalname || '';
                        return (
                          <li
                            key={i}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setAddForm(f => ({ ...f, assigned_to: displayName }));
                              setUserSearchQuery('');
                              setUserSuggestions([]);
                            }}
                          >
                            {displayName}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div className="asset-modal-actions">
                <button type="button" onClick={closeAddModal} className="asset-modal-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={addSubmitting} className="asset-modal-submit">
                  {addSubmitting ? 'Adding...' : 'Add Asset'}
                </button>
              </div>
            </form>
            ) : (
            <form onSubmit={handleImportFromJson} className="asset-import-form">
              {addError && <div className="asset-modal-error">{addError}</div>}
              <div className="asset-modal-row">
                <label>JSON (paste or upload file)</label>
                <textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder='{"Peripheral":[{"MachineType":"...","SystemManufacturer":"...","SerialNumber":"...","ChassisName":"Monitor",...}]}'
                  rows={10}
                  className="asset-import-textarea"
                />
                <div className="asset-import-file-row">
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={handleJsonFileSelect}
                    className="asset-import-file-input"
                  />
                  <span className="asset-import-file-hint">Field mapping: SystemManufacturer → Manufacturer, ChassisName → Chassis Type, MachineType → Model, SerialNumber → Serial, Discovered → Last Connected, UPN (root) → upn</span>
                </div>
              </div>
              {importResults && (
                <div className="asset-import-results">
                  <div className="asset-import-success">
                    Added: {importResults.success.length} ({importResults.success.map(s => s.serial).join(', ')})
                  </div>
                  {importResults.failed.length > 0 && (
                    <div className="asset-import-failed">
                      Failed: {importResults.failed.length}
                      <ul>
                        {importResults.failed.map((f, i) => (
                          <li key={i}>{f.item?.SerialNumber || f.item?.MachineType || '?'}: {f.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <div className="asset-modal-actions">
                <button type="button" onClick={closeAddModal} className="asset-modal-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={addSubmitting} className="asset-modal-submit">
                  {addSubmitting ? 'Importing...' : 'Import devices'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;

